import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "wouter";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/ui/logo";
import { fadeIn } from "@/lib/animations";
import { useSmoothScroll } from "@/hooks/use-smooth-scroll";

export function Navbar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { handleLinkClick } = useSmoothScroll({
    offset: 80, // Offset for fixed header
    duration: 1000 // Smoother, slower animation
  });

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };
  
  // Combined handler for mobile menu links
  const handleMobileClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    closeMobileMenu();
    handleLinkClick(e);
  };

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 10;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [scrolled]);

  return (
    <motion.nav
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled
          ? "bg-gray-950/80 backdrop-blur shadow-md border-b border-gray-800/30"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link href="/">
            <div className="flex-shrink-0 flex items-center cursor-pointer">
              <Logo className="h-8 w-auto" />
              <span className="ml-2 text-2xl font-light text-white">Vettor<span className="text-violet-400">.ai</span></span>
            </div>
          </Link>
          <div className="hidden md:block">
            <div className="ml-10 flex items-center space-x-4">
              <a
                href="#how-it-works"
                onClick={handleLinkClick}
                className="px-3 py-2 rounded-md text-sm font-light text-gray-300 hover:text-violet-300 transition-colors"
              >
                How It Works
              </a>
              <a
                href="#benefits"
                onClick={handleLinkClick}
                className="px-3 py-2 rounded-md text-sm font-light text-gray-300 hover:text-violet-300 transition-colors"
              >
                Benefits
              </a>
              <a
                href="#testimonials"
                onClick={handleLinkClick}
                className="px-3 py-2 rounded-md text-sm font-light text-gray-300 hover:text-violet-300 transition-colors"
              >
                Testimonials
              </a>
              <a href="#contact" onClick={handleLinkClick}>
                <Button variant="default" className="bg-violet-600 hover:bg-violet-700 text-white border-0 font-light">
                  Request Access
                </Button>
              </a>
            </div>
          </div>
          <div className="md:hidden">
            <button
              onClick={toggleMobileMenu}
              className="bg-gray-800 rounded-md p-2 inline-flex items-center justify-center text-gray-300 hover:text-violet-300 hover:bg-gray-700 focus:outline-none"
              aria-label="Main menu"
              aria-expanded="false"
            >
              {mobileMenuOpen ? (
                <X className="block h-6 w-6" aria-hidden="true" />
              ) : (
                <Menu className="block h-6 w-6" aria-hidden="true" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.3 }}
          className="md:hidden"
        >
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-gray-900 shadow-lg border border-gray-800">
            <a
              href="#how-it-works"
              onClick={handleMobileClick}
              className="block px-3 py-2 rounded-md text-base font-light text-gray-300 hover:text-violet-300 hover:bg-gray-800"
            >
              How It Works
            </a>
            <a
              href="#benefits"
              onClick={handleMobileClick}
              className="block px-3 py-2 rounded-md text-base font-light text-gray-300 hover:text-violet-300 hover:bg-gray-800"
            >
              Benefits
            </a>
            <a
              href="#testimonials"
              onClick={handleMobileClick}
              className="block px-3 py-2 rounded-md text-base font-light text-gray-300 hover:text-violet-300 hover:bg-gray-800"
            >
              Testimonials
            </a>
            <a
              href="#contact"
              onClick={handleMobileClick}
              className="block px-3 py-2 rounded-md text-base font-light text-white bg-violet-600 hover:bg-violet-700 text-center"
            >
              Request Access
            </a>
          </div>
        </motion.div>
      )}
    </motion.nav>
  );
}
